import { BULB_MAX_ENERGY } from '../config/balance.js';
import type { ColorId } from '../config/colors.js';
import {
  buildNetworkAnalysis,
  buildingConnectionCells,
  findCellPathToNode,
  isNodeReachableFromGenerator,
  orientCableFromCell,
  pathToCableRoute,
} from './network.js';
import type { Cable, CellCoord, GameNode, GameState } from './types.js';

export interface RouteCache {
  version: number;
  /** generatorId:color -> reachable bulb ids (refreshed on topology change) */
  reachable: Map<string, string[]>;
}

export function createRouteCache(): RouteCache {
  return { version: -1, reachable: new Map() };
}

export function invalidateRouting(cache: RouteCache, version: number): void {
  cache.version = version;
  cache.reachable.clear();
}

function cacheKey(generatorId: string, color: ColorId): string {
  return `${generatorId}:${color}`;
}

function bulbMaxEnergy(state: GameState): number {
  return BULB_MAX_ENERGY * state.runModifiers.bulbCapacityMult;
}

/** Cables attached to a generator (with at least one segment). */
export function cablesAttachedToGenerator(
  state: GameState,
  generatorId: string,
): Cable[] {
  return state.cables
    .filter(
      (c) =>
        c.cells.length >= 2 &&
        (c.endpointA === generatorId || c.endpointB === generatorId),
    )
    .sort((a, b) => a.id.localeCompare(b.id));
}

function listReachableBulbs(
  state: GameState,
  cache: RouteCache,
  generator: GameNode,
  color: ColorId,
): GameNode[] {
  if (cache.version !== state.networkVersion) {
    cache.reachable.clear();
    cache.version = state.networkVersion;
  }

  const key = cacheKey(generator.id, color);
  let ids = cache.reachable.get(key);
  if (!ids) {
    const analysis = buildNetworkAnalysis(state);
    ids = state.nodes
      .filter(
        (n) =>
          n.kind === 'bulb' &&
          n.color === color &&
          isNodeReachableFromGenerator(state, analysis, generator, n),
      )
      .map((n) => n.id);
    ids.sort();
    cache.reachable.set(key, ids);
  }

  return ids
    .map((id) => state.nodes.find((n) => n.id === id))
    .filter((n): n is GameNode => !!n)
    .filter((n) => (n.energy ?? 0) < bulbMaxEnergy(state) - 0.01);
}

/** Legacy helper — any reachable hungry bulb (no cursor bump). */
export function pickDestinationBulb(
  state: GameState,
  cache: RouteCache,
  generator: GameNode,
  color: ColorId,
): GameNode | null {
  const bulbs = listReachableBulbs(state, cache, generator, color);
  return bulbs[0] ?? null;
}

/** Number of same-color bulbs currently reachable from this generator (any charge). */
export function countConnectedBulbs(state: GameState, generator: GameNode): number {
  if (generator.kind !== 'generator' || !generator.color) return 0;
  const analysis = buildNetworkAnalysis(state);
  let n = 0;
  for (const node of state.nodes) {
    if (node.kind !== 'bulb' || node.color !== generator.color) continue;
    if (isNodeReachableFromGenerator(state, analysis, generator, node)) n++;
  }
  return n;
}

function cellsEqual(a: CellCoord, b: CellCoord): boolean {
  return a.x === b.x && a.y === b.y;
}

/**
 * Build a cell path that is forced to leave the generator on `preferred` cable.
 */
function pathViaPreferredCable(
  state: GameState,
  generator: GameNode,
  targetBulb: GameNode,
  preferred: Cable,
): CellCoord[] | null {
  const analysis = buildNetworkAnalysis(state);
  const genCell = buildingConnectionCells(generator)[0]!;
  const onGen = preferred.cells.some((c) => cellsEqual(c, genCell));
  if (!onGen && preferred.endpointA !== generator.id && preferred.endpointB !== generator.id) {
    return null;
  }

  // Direct: this cable alone reaches the bulb
  const otherEnd =
    preferred.endpointA === generator.id
      ? preferred.endpointB
      : preferred.endpointB === generator.id
        ? preferred.endpointA
        : null;
  if (otherEnd === targetBulb.id) {
    const orient = orientCableFromCell(preferred, genCell);
    return orient.forward
      ? preferred.cells.map((c) => ({ ...c }))
      : [...preferred.cells].reverse().map((c) => ({ ...c }));
  }

  const orient = orientCableFromCell(preferred, genCell);
  const along = orient.forward
    ? preferred.cells.map((c) => ({ ...c }))
    : [...preferred.cells].reverse().map((c) => ({ ...c }));
  const far = along[along.length - 1]!;
  const rest = findCellPathToNode(
    state,
    analysis,
    far,
    targetBulb,
    generator.color ?? null,
  );
  if (!rest || rest.length === 0) return null;

  const full = [...along];
  for (const c of rest) {
    const last = full[full.length - 1]!;
    if (cellsEqual(last, c)) continue;
    full.push({ ...c });
  }
  return full;
}

export function buildPulseRoute(
  state: GameState,
  generator: GameNode,
  targetBulb: GameNode,
  preferStartCableId?: string,
): { routeCableIds: string[]; startCableId: string } | null {
  const analysis = buildNetworkAnalysis(state);

  if (preferStartCableId) {
    const preferred = state.cables.find((c) => c.id === preferStartCableId);
    if (preferred) {
      const via = pathViaPreferredCable(state, generator, targetBulb, preferred);
      if (via && via.length >= 2) {
        let routeCableIds = pathToCableRoute(via, state.cables, state);
        if (routeCableIds && routeCableIds.length > 0) {
          if (routeCableIds[0] !== preferred.id) {
            routeCableIds = [
              preferred.id,
              ...routeCableIds.filter((id) => id !== preferred.id),
            ];
          }
          return { routeCableIds, startCableId: preferred.id };
        }
        // Path stays on preferred cable only
        return { routeCableIds: [preferred.id], startCableId: preferred.id };
      }
    }
  }

  const genCells = buildingConnectionCells(generator);
  let bestPath: ReturnType<typeof findCellPathToNode> = null;

  for (const gc of genCells) {
    const path = findCellPathToNode(
      state,
      analysis,
      gc,
      targetBulb,
      generator.color ?? null,
    );
    if (path && (!bestPath || path.length < bestPath.length)) {
      bestPath = path;
    }
  }

  if (!bestPath || bestPath.length < 2) return null;
  const routeCableIds = pathToCableRoute(bestPath, state.cables, state);
  if (!routeCableIds || routeCableIds.length === 0) return null;

  return { routeCableIds, startCableId: routeCableIds[0]! };
}

export interface PulseLaunch {
  target: GameNode;
  startCableId: string;
}

/** Soft hunger weight: empty ≈ 1.25, nearly full ≈ 0.25 — no monopoly. */
export function bulbHungerWeight(energy: number, maxEnergy: number): number {
  const fill = Math.max(0, Math.min(1, energy / Math.max(1e-6, maxEnergy)));
  const hunger = 1 - fill;
  return hunger * hunger + 0.25;
}

/**
 * Soft priority among reachable hungry bulbs: lower fill % gets higher
 * weight (hunger² + 0.25). Deterministic roulette via pulseRouteCursor so
 * emptier bulbs get more pulses without monopolizing the generator.
 */
export function pickPulseLaunch(
  state: GameState,
  cache: RouteCache,
  generator: GameNode,
): PulseLaunch | null {
  if (!generator.color) return null;
  const bulbs = listReachableBulbs(state, cache, generator, generator.color);
  if (bulbs.length === 0) return null;

  const maxE = bulbMaxEnergy(state);
  const weighted = bulbs.map((bulb) => ({
    bulb,
    weight: bulbHungerWeight(bulb.energy ?? 0, maxE),
  }));
  const totalW = weighted.reduce((s, w) => s + w.weight, 0);
  const cursor = state.pulseRouteCursor[generator.id] ?? 0;

  // Primary pick: knuth-multiplicative hash so consecutive pulses mix
  // across the weight space (avoids long same-bulb streaks).
  const mixed = ((Math.imul(cursor, 2654435761) >>> 0) / 4294967296) * totalW;
  let slot = mixed;
  let primaryIdx = 0;
  for (let i = 0; i < weighted.length; i++) {
    slot -= weighted[i]!.weight;
    if (slot <= 0) {
      primaryIdx = i;
      break;
    }
  }

  // Try primary first, then remaining by descending weight (route failures).
  const order = [
    primaryIdx,
    ...weighted
      .map((_, i) => i)
      .filter((i) => i !== primaryIdx)
      .sort((a, b) => weighted[b]!.weight - weighted[a]!.weight),
  ];

  for (const idx of order) {
    const bulb = weighted[idx]!.bulb;
    const route = buildPulseRoute(state, generator, bulb);
    if (route) {
      state.pulseRouteCursor[generator.id] = cursor + 1;
      return { target: bulb, startCableId: route.startCableId };
    }
  }
  return null;
}

/** True if generator has at least one cable that can reach a hungry bulb. */
export function canLaunchPulse(
  state: GameState,
  cache: RouteCache,
  generator: GameNode,
): boolean {
  if (!generator.color) return false;
  const cables = cablesAttachedToGenerator(state, generator.id);
  if (cables.length === 0) return false;
  return listReachableBulbs(state, cache, generator, generator.color).length > 0;
}
